<template>
  <div class="animated fadeIn page-container">

    <!-- Navigation tabs -->
    <b-nav justified tabs class="mb-4">
      <b-nav-item v-for="(item, i) in section.choices"
                  :key="`section-${i}`"
                  :active="section.value === item.value"
                  @click="setActiveSection(item.value)">
        <h2>{{ item.title }}</h2>
      </b-nav-item>
    </b-nav>

    <!-- Tabs content -->
    <h3>{{ sectionDescription }}</h3>

    <congestion-filters-old
      :section="section.value"
      :api-request-data="apiRequestData"
      @update="applyFilterHandler"/>

    <b-row>
      <b-col cols="12">
        <timeseries-chart
          title="Vessel Count"
          :y-axis="{titleText: 'Congestion (# of vessels)'}"
          :export-types="exportTypes"
          @export="(format) => handleExport(format)"
          :dates="sectionDates"
          :values="stats[currentSection].vesselCount" />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <timeseries-chart
          title="Average Waiting Time (days)"
          :y-axis="{titleText: 'Average Waiting Time (days)'}"
          :export-types="exportTypes"
          @export="(format) => handleExport(format)"
          values-format="float1"
          :dates="sectionDates"
          :values="stats[currentSection].waitingDaysAverage" />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <timeseries-chart
          title="Vessel DWT"
          :y-axis="{titleText: 'Congestion (million DWT)'}"
          :export-types="exportTypes"
          @export="(format) => handleExport(format)"
          :dates="sectionDates"
          :values="stats[currentSection].vesselDwt" />
      </b-col>
    </b-row>

    <export-modal
      v-model="csvModal.show"
      file-type="csv"
      :file-link="csvModal.link"
      :alert-text="csvModal.alertText"/>

  </div>
</template>

<script>
import { mapActions } from 'vuex'
import getNested from 'lodash-es/get'
import debounce from 'lodash-es/debounce'
import isEmpty from 'lodash-es/isEmpty'
import moment from 'moment'
import { downloadAsFile } from '@/shared/download-utils'

import TimeseriesChart from '../components/widgets/TimeseriesChart'
import CongestionFiltersOld from '../components/congestion/CongestionFiltersOld'
import ExportModal from '../components/widgets/ExportModal'


export default {
  name: 'congestion',
  components: {
    ExportModal,
    CongestionFiltersOld,
    TimeseriesChart,
  },
  data() {
    return {
      // tabs management
      section: {
        value: 'port',
        choices: [
          {
            value: 'port',
            title: 'Port Congestion',
            description: 'Port Level Congestion Statistics',
          },
          {
            value: 'country',
            title: 'Country Congestion',
            description: 'Country Level Congestion Statistics',
          },
          {
            value: 'region',
            title: 'Regional Congestion',
            description: 'Region Level Congestion Statistics',
          },
        ],
      },

      filterValues: {},

      valuesField: [
        'date',
        'vesselCount',
        'waitingDaysAverage',
        'vesselDwt'
      ],

      stats: {
        port: {
          filterField: 'portId',
          date: [],
          vesselCount: [],
          waitingDaysAverage: [],
          vesselDwt: [],
        },
        region: {
          filterField: 'regionId',
          date: [],
          vesselCount: [],
          waitingDaysAverage: [],
          vesselDwt: [],
        },
        country: {
          filterField: 'countryCode',
          date: [],
          vesselCount: [],
          waitingDaysAverage: [],
          vesselDwt: [],
        },
      },

      apiRequestData: {},
      exportTypes: ['csv'],  // buttons to export data in a specific format
      csvModal: {
        show: false,
        link: null,
        alertText: null,
      },
    };
  },
  methods: {
    ...mapActions('api/congestion', [
      'getPortCongestionStats',
      'getCountryCongestionStats',
      'getRegionCongestionStats',
    ]),

    setActiveSection(sectionName) {
      this.section.value = sectionName;
    },

    applyFilterHandler: debounce(
      // for more clear reading
      async function (filterValues) {
        return await this.applyFilter(filterValues);
      },
      400
    ),

    applyFilter: async function (filterValues) {
      window.analytics.track('CongestionSearch');
      // console.log('applyFilter (congestion)', JSON.stringify(filterValues, null, 2));

      this.filterValues = filterValues;  // used in .handleExport()

      if(this.isFilterValid(filterValues)) {
        const response = await this.getAPIData(filterValues);

        let targetSection = this.stats[this.currentSection];
        this.valuesField.forEach(fieldName => targetSection[fieldName] = []);

        getNested(response, 'data.congestions', []).forEach(item => {
          this.valuesField.forEach(fieldName => {
            targetSection[fieldName].push(item[fieldName])
          });
        });

        this.apiRequestData = {[this.currentSection]: response.requestData};
      } else {
        this.resetCurrentSection();
      }
    },

    isFilterValid(filterValues) {
      let requiredAPIField = this.stats[this.currentSection].filterField;

      return (
        !isEmpty(filterValues.segment) &&
        !isEmpty(filterValues[requiredAPIField])
      );
    },

    resetCurrentSection() {
      // console.log('resetCurrentSection');
      this.valuesField.forEach(fieldName => {
        this.stats[this.currentSection][fieldName] = [];
        // this.$set(this.stats[this.currentSection], fieldName, []);
      });
    },

    async getAPIData(filterValues, extraParams) {
      const methodsMap = {
        port: this.getPortCongestionStats,
        country: this.getCountryCongestionStats,
        region: this.getRegionCongestionStats,
      };

      const params = {...filterValues, ...extraParams};
      return await methodsMap[this.currentSection](params);
    },

    async handleExport(format) {
      let response = await this.getAPIData(this.filterValues, {format});

      if(getNested(response, 'data.csv')) {
        this.csvModal.link = response.data.csv
        this.csvModal.alertText = null;
        this.csvModal.show = true;
      } else {
        const timestamp = moment().format('YYYY-MM-DD-HH-mm');
        downloadAsFile(response, `congestion_export_${timestamp}.${format}`);
      }
    },
  },

  computed: {
    sectionDescription() {
      let section = this.section.choices.find(
        section => section.value === this.currentSection
      );
      return section ? section.description : 'Unknown section';
    },

    currentSection() {
      return this.section.value;
    },

    sectionDates() {
      return getNested(this.stats, `${this.currentSection}.date`, []);
    },
  },
};
</script>
<style scoped>
.page-container >>> .card-content {
  min-height: 460px;
}
</style>
